import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, PackageOpen, MapPin, ChevronRight, ClipboardCheck, Recycle, Sparkles, HelpCircle, CalendarClock, Boxes } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';

const VideMaisonAnnecy = () => {
  const quartiers = [
    {
      nom: 'Les Fins',
      description: "Quartier résidentiel du nord d'Annecy, construit pour l'essentiel entre les années 50 et 70. Les maisons familiales y disposent de jardins, de garages et de combles aménagés que les propriétaires de longue date ont remplis au fil des décennies. C'est le secteur où nous réalisons les vide-maisons les plus complets avant remise en vente."
    },
    {
      nom: 'La Visitation et Trésum',
      description: "Sur les coteaux dominant la Vieille Ville, autour de la basilique de la Visitation, s'étagent villas anciennes et demeures bourgeoises. Les ruelles pentues et étroites limitent l'approche du camion : nos équipes organisent un portage relais depuis le point de stationnement le plus proche, planifié dès la visite de devis."
    },
    {
      nom: 'Seynod',
      description: "Commune déléguée au sud-ouest d'Annecy, dominée par les lotissements pavillonnaires des années 70 à 90 (secteurs de Champ Fleuri, Vieugy, Barral). Ces maisons avec sous-sol complet et garage double génèrent des volumes importants : un vide-maison intégral y représente souvent 40 à 60 m³ à trier et évacuer."
    },
    {
      nom: 'Meythet',
      description: "Ancienne commune ouvrière devenue résidentielle, proche de l'aérodrome d'Annecy. Beaucoup de maisons de lotissement y sont encore occupées par leurs premiers propriétaires. Les vide-maisons y accompagnent fréquemment un départ en résidence senior : nous procédons avec méthode, pièce par pièce, en présence de la famille."
    },
    {
      nom: 'Vallin-Fier et les bords du Fier',
      description: "À Cran-Gevrier, l'ancien secteur industriel des bords du Fier se transforme en écoquartier. Les maisons anciennes promises à rénovation ou démolition doivent être vidées intégralement avant travaux. Nous intervenons en coordination avec les promoteurs et les artisans pour libérer les lieux dans les délais du chantier."
    },
    {
      nom: 'Albigny et le Petit Port',
      description: "Côté Annecy-le-Vieux, entre la plage d'Albigny et le Petit Port, les maisons de caractère proches du lac se vendent vite et cher. Les propriétaires nous sollicitent pour un vide-maison express entre la signature du compromis et la remise des clés, souvent en moins de deux semaines."
    }
  ];

  const etapes = [
    { icon: Phone, step: '1', title: 'Échange initial', desc: 'Vous nous décrivez la maison, son contenu et votre échéance (vente, déménagement, travaux). Nous fixons une visite sous 24 à 48h.' },
    { icon: ClipboardCheck, step: '2', title: 'Visite et devis ferme', desc: 'Nous parcourons toutes les pièces, du grenier au sous-sol, dépendances comprises. Le devis remis est gratuit, détaillé et sans surprise.' },
    { icon: Boxes, step: '3', title: 'Tri et mise de côté', desc: 'Les objets que vous conservez sont isolés et protégés. Les biens valorisables sont identifiés pour le réemploi avant toute évacuation.' },
    { icon: Recycle, step: '4', title: 'Évacuation totale', desc: 'La maison est vidée intégralement en 1 à 2 jours selon le volume. Don aux associations locales, recyclage par matière, déchèterie en dernier recours.' },
    { icon: Sparkles, step: '5', title: 'Maison prête à livrer', desc: 'Balayage final de toutes les pièces et dépendances. Vous récupérez une maison entièrement vide, prête pour la vente, les travaux ou l\'état des lieux.' }
  ];

  const avantages = [
    {
      title: 'Des délais calés sur le marché immobilier annécien',
      description: 'À Annecy, un compromis se signe vite et les acheteurs veulent une maison vide à la remise des clés. Nous planifions le vide-maison en fonction de votre date de signature, y compris en urgence sous 48h.'
    },
    {
      title: 'Une connaissance fine des maisons du bassin',
      description: 'Lotissements avec sous-sol complet de Seynod, villas pentues de la Visitation, maisons de caractère d\'Albigny : nous savons à l\'avance quel matériel et combien d\'intervenants mobiliser pour chaque configuration.'
    },
    {
      title: 'Valorisation systématique avant évacuation',
      description: 'Un vide-maison ne se résume pas à tout jeter. Mobilier, électroménager, livres, outillage : ce qui a de la valeur ou peut resservir est identifié lors de la visite et orienté vers le réemploi local plutôt que la benne.'
    },
    {
      title: 'Un seul interlocuteur du devis à la remise des clés',
      description: 'Vous n\'avez rien à coordonner : tri, démontage des meubles, portage, évacuation, balayage final. Notre équipe gère l\'intégralité du chantier et vous tient informé à chaque étape, même à distance.'
    }
  ];

  const faq = [
    {
      question: 'Quelle est la différence entre un vide-maison et un débarras classique ?',
      answer: 'Le débarras peut être partiel : on évacue certains meubles, une cave ou un garage. Le vide-maison est intégral : la maison est restituée entièrement vide, de la boîte aux lettres au grenier, y compris les dépendances et le jardin. C\'est la prestation adaptée avant une vente immobilière, un déménagement définitif ou de gros travaux.'
    },
    {
      question: 'Pouvez-vous vider une maison à Seynod ou Meythet avant une vente qui se conclut rapidement ?',
      answer: 'Oui, c\'est une situation très fréquente dans ces communes déléguées où les maisons de lotissement partent en quelques semaines. Entre le compromis et l\'acte authentique, nous planifions le vide-maison intégral à la date qui vous arrange, avec une intervention possible sous 48h en cas d\'urgence.'
    },
    {
      question: 'Comment accédez-vous aux villas de la Visitation ou de Trésum, difficiles d\'accès en camion ?',
      answer: 'Les coteaux au-dessus de la Vieille Ville sont notre quotidien. Lors de la visite de devis, nous repérons le point de stationnement le plus proche et organisons un portage relais avec du matériel adapté (diables tout-terrain, sangles, protections). Ce surcoût logistique est intégré au devis initial, jamais facturé après coup.'
    },
    {
      question: 'Que devient le mobilier en bon état lors d\'un vide-maison à Annecy ?',
      answer: 'Rien de réutilisable ne part à la benne. Les meubles, l\'électroménager fonctionnel et les objets en état d\'usage sont orientés vers les associations et ressourceries du bassin annécien. Les pièces présentant une valeur marchande sont signalées aux propriétaires, qui décident de leur sort avant l\'évacuation.'
    },
    {
      question: 'Combien coûte un vide-maison complet à Annecy ?',
      answer: 'Le tarif dépend du volume total (pièces de vie, sous-sol, grenier, dépendances), de l\'accessibilité et de la part d\'objets valorisables. Pour une maison familiale du bassin annécien vidée intégralement, comptez généralement entre 900 et 3 000 euros. Le devis établi sur place est gratuit et le prix annoncé est ferme.'
    },
    {
      question: 'Dois-je être présent pendant toute la durée du vide-maison ?',
      answer: 'Non. Beaucoup de nos clients travaillent à Genève ou résident hors de Haute-Savoie. Après la visite initiale et la validation de la liste des objets à conserver, nous pouvons intervenir seuls avec un jeu de clés, vous envoyer des photos d\'avancement et restituer les clés à l\'agence immobilière ou au notaire.'
    }
  ];

  const communesVoisines = [
    { nom: 'Seynod' },
    { nom: 'Meythet' },
    { nom: 'Cran-Gevrier' },
    { nom: 'Annecy-le-Vieux' },
    { nom: 'Poisy' },
    { nom: 'Epagny-Metz-Tessy' },
    { nom: 'Argonay' },
    { nom: 'Pringy' },
    { nom: 'Sevrier' },
    { nom: 'Saint-Jorioz' },
    { nom: 'Duingt' },
    { nom: 'Veyrier-du-Lac' },
    { nom: 'Alby-sur-Chéran' },
    { nom: 'Rumilly' }
  ];

  const jsonLdLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://debeco.fr/#organization",
    "name": "DEBECO - Débarras & Recyclage",
    "description": "Vide-maison intégral à Annecy et dans le bassin annécien : tri, évacuation complète, valorisation du mobilier et recyclage responsable en Haute-Savoie.",
    "url": "https://debeco.fr/vide-maison-annecy",
    "telephone": "+33451401617",
    "email": "contact@debeco.fr",
    "image": "https://debeco.fr/images/vide-maison-annecy.jpg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4 Rue des Frères de Montgolfier",
      "addressLocality": "Annecy",
      "addressRegion": "Haute-Savoie",
      "postalCode": "74600",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "City",
      "name": "Annecy",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Haute-Savoie"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Vide-maison",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Vide-maison à Annecy",
            "description": "Vidage intégral de maison à Annecy avant vente, déménagement, travaux ou départ en résidence senior : tri, évacuation totale, valorisation et balayage final."
          }
        }
      ]
    },
    "priceRange": "€€"
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://debeco.fr/services" },
      { "@type": "ListItem", "position": 3, "name": "Débarras de maison", "item": "https://debeco.fr/services/maison" },
      { "@type": "ListItem", "position": 4, "name": "Vide-maison Annecy", "item": "https://debeco.fr/vide-maison-annecy" }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdLocalBusiness, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-managed', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Vide-maison à Annecy (Haute-Savoie) | DEBECO Débarras"
        description="Vide-maison intégral à Annecy : tri, évacuation totale et valorisation du mobilier. Devis gratuit, intervention rapide sous 48-72h. Tél. 04 51 40 16 17."
        canonical="/vide-maison-annecy"
        ogImage="/images/vide-maison-annecy.jpg"
      />

      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services" className="hover:text-emerald-600">Services</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services/maison" className="hover:text-emerald-600">Débarras de maison</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Vide-maison Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="local-h1">Vide-maison à Annecy</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Votre maison rendue entièrement vide, du grenier aux dépendances, dans les délais de votre vente, de votre déménagement ou de vos travaux. Tout le bassin annécien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-devis">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-phone">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section: Notre service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/vide-maison-annecy.jpg"
                  webpSrc="/images/vide-maison-annecy.webp"
                  alt="Vide-maison à Annecy : salon d'une maison familiale en cours de vidage complet avec cartons et mobilier ancien, vue sur le lac et les montagnes"
                  className="w-full h-full object-cover"
                  width="1264"
                  height="848"
                  loading="eager"
                  data-testid="local-vide-maison-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <PackageOpen className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Notre service de vide-maison à Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Un vide-maison, ce n'est pas un simple débarras : c'est l'engagement de restituer une maison entièrement vide, à une date précise. À Annecy, cette échéance est presque toujours dictée par le calendrier immobilier — un compromis signé aux Fins ou à Albigny, une mutation professionnelle vers Genève, un départ en résidence senior à Meythet ou le lancement d'un chantier de rénovation à Vallin-Fier.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  DEBECO prend en charge l'intégralité de l'opération : tri de tout le contenu, mise de côté de ce que vous conservez, démontage des meubles, évacuation totale — pièces de vie, cuisine équipée, sous-sol, grenier, garage, abri de jardin — puis balayage final. Vous remettez les clés d'une maison propre et vide, sans avoir soulevé un carton.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Le bâti du bassin annécien exige une vraie préparation : sous-sols complets des pavillons de Seynod, combles chargés des maisons des Fins, accès pentus des villas de la Visitation. Chaque configuration est évaluée lors de la visite gratuite, pour un devis ferme et une intervention sans imprévu.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Quartiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Les quartiers d'Annecy où nous réalisons des vide-maisons</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Du nord résidentiel aux coteaux de la Visitation, chaque secteur du bassin annécien impose sa logistique. Voici comment nous intervenons quartier par quartier.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quartiers.map((q, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <h3 className="font-bold text-lg text-blue-900">{q.nom}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{q.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Section: Comment ca se passe */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Comment se déroule un vide-maison à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {etapes.map((e) => (
                <div key={e.step} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <e.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Étape {e.step}</div>
                  <h3 className="font-bold text-blue-900 mb-2">{e.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{e.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pourquoi choisir DEBECO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Pourquoi confier votre vide-maison à DEBECO à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {avantages.map((a, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border-2 border-transparent hover:border-emerald-500 transition-all">
                  <CheckCircle className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{a.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{a.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Zones desservies */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy-maisons-vue-aerienne.jpg"
                  webpSrc="/images/annecy-maisons-vue-aerienne.webp"
                  alt="Maisons du bassin annécien vues du ciel, secteur d'intervention DEBECO pour le vide-maison à Annecy"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="801"
                  loading="lazy"
                  data-testid="local-annecy-image"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Zones desservies autour d'Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Nos équipes de vide-maison rayonnent sur toute l'agglomération : communes déléguées d'Annecy, rive ouest et rive est du lac, plateau des Bornes et vallée du Chéran, aux mêmes conditions tarifaires qu'en centre-ville.
                </p>
                <div className="flex flex-wrap gap-2">
                  {communesVoisines.map((c, index) => (
                    <span key={index} className="bg-blue-100 text-blue-900 px-3 py-1.5 rounded-full text-sm font-medium">
                      {c.nom}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <Link to="/zones/annecy" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    Voir toute notre zone d'intervention Annecy
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                  <Link to="/services/maison" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    Découvrir notre service débarras de maison
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-3xl font-bold text-blue-900">Questions fréquentes — Vide-maison</h2>
            </div>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 transition-all">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-blue-900 mb-3">{item.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-white" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras à Annecy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/debarras-maison-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Évacuation partielle ou ciblée, quartier par quartier</p>
            </Link>
            <Link to="/debarras-succession-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras succession à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Accompagnement respectueux, coordination notaire</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Guide des prix de débarras à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Fourchettes tarifaires détaillées pour 2026</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un vide-maison à Annecy ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Confiez-nous votre maison : visite gratuite, devis ferme et restitution entièrement vide à la date convenue, partout dans le bassin annécien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-devis-final">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-phone-final">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VideMaisonAnnecy;
